import React from "react";
import { Link,useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from '../../assets/Logo-Blue-white.webp';
import { FaLinkedinIn } from "react-icons/fa";
import { FiMail } from "react-icons/fi";

/**
 * VizcornFooter
 * ------------------------------------------------------------------
 * Full-bleed black footer: logo + description + address on the left,
 * nav columns, "Let's talk" CTA pinned top-right, a dotted divider,
 * then legal links and social icons along the bottom.
 *
 * All colors are inline styles (not Tailwind arbitrary-value classes)
 * and all icons are self-contained inline SVGs — nothing here depends
 * on lucide-react or any icon package, so it can't break if a named
 * icon export changes or is missing in your installed version.
 *
 * Nav column links do a real smooth-scroll to their section (same
 * pattern as Navbar.jsx); "Careers" opens the shared <CareersModal />
 * instead of linking anywhere; legal links route through the current
 * language prefix (/en/terms, /de/terms, etc).
 *
 * Usage:
 *   <VizcornFooter />
 *   <VizcornFooter email="hello@vizcorn.com" />
 * ------------------------------------------------------------------
 */

function scrollTo(id) {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}


function IconArrowUp(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </svg>
  );
}

const SOCIALS = [
  {
    Icon: FiMail,
    href: `mailto:contact@vizcorn.com`,
    label: "Email",
  },
  {
    Icon: FaLinkedinIn,
    href: "https://www.linkedin.com/company/vizcornkft/",
    label: "LinkedIn",
  },
];

export default function VizcornFooter({ email = "contact@vizcorn.com" }) {
  const { t } = useTranslation();
   const navigate = useNavigate();
  const { lang } = useParams();

  const COLUMNS = [
    {
      title: t('footerNew.columns.company.title'),
      links: [
        { label: t('footerNew.columns.company.weAre'), onClick: () => scrollTo('AboutSection') },
        { label: t('footerNew.columns.company.whyVizcorn'), onClick: () => scrollTo('WhySection') },
        { label: t('footerNew.columns.company.careers'),  onClick: () => navigate(`/${lang}/careers`), },
      ],
    },
    {
      title: t('footerNew.columns.services.title'),
      links: [
        { label: t('footerNew.columns.services.weDeliver'), onClick: () => scrollTo('DesignServices') },
        { label: t('footerNew.columns.services.buildTogether'), onClick: () => scrollTo('ContactUs') },
      ],
    },
    {
      title: t('footerNew.columns.support.title'),
      links: [
        { label: t('footerNew.columns.support.contactUs'), onClick: () => scrollTo('ContactUs') },
      ],
    },
  ];

  const LEGAL_LINKS = [
    { label: t('footerNew.legal.cookies'), to: `/${lang}/cookies` },
    { label: t('footerNew.legal.terms'), to: `/${lang}/terms` },
    { label: t('footerNew.legal.privacy'), to: `/${lang}/privacy` },
  ];

  const ADDRESS_LINES = [t('footerNew.addressLine1'), t('footerNew.addressLine2')];



  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="w-full" style={{ backgroundColor: "#000000" }}>
      <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10 sm:py-16">
        {/* top row: logo (+ description/address) + CTA */}
        <div className="flex flex-row flex-wrap items-start justify-between gap-x-8 gap-y-10">
          <div className="max-w-sm">
            <Link to={`/${lang}`}>
              <img
                src={logo}
                alt="Vizcorn"
                width={200}
                height={62}
                loading="lazy"
                decoding="async"
                style={{ maxWidth: '200px' }}
                className="max-w-full max-h-full object-contain p-0.5"
              />
            </Link>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
              {t('footerNew.description')}
            </p>
            <address className="mt-4 not-italic text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>
              {ADDRESS_LINES.map((line, idx) => (
                <div key={`address-${idx}`}>{line}</div>
              ))}
            </address>
          </div>

          {/* CTA — always pinned right */}
          <a href={`mailto:${email}`} className="block shrink-0 text-right">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "rgba(255,255,255,0.6)" }}>
              {t('footerNew.ctaLabel')}
            </div>
            <div className="mt-1 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl" style={{ color: "#FFFFFF" }}>
              {t('footerNew.ctaTitle')}
            </div>
          </a>
        </div>

        {/* nav columns */}
        <div className="mt-12 grid grid-cols-2 gap-x-10 gap-y-8 sm:grid-cols-3 sm:max-w-md">
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: "rgba(255,255,255,0.55)" }}>
                {col.title}
              </div>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <button
                      onClick={l.onClick}
                      className="text-[15px] font-medium transition-opacity hover:opacity-75 text-left"
                      style={{ color: "#FFFFFF" }}
                    >
                      {l.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* dotted divider */}
        <div className="my-10" style={{ borderTop: "1.5px dotted rgba(255,255,255,0.25)" }} />

        {/* bottom row: legal + socials */}
        <div className="flex flex-col-reverse items-start gap-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {LEGAL_LINKS.map((l) => (
              <Link key={l.label} to={l.to} className="text-sm font-medium transition-opacity hover:opacity-75" style={{ color: "rgba(255,255,255,0.75)" }}>
                {l.label}
              </Link>
            ))}
          </div>

         <div className="flex items-center gap-3">
  {SOCIALS.map(({ Icon, href, label }) => (
  <a
    key={label}
    href={href}
    target={href.startsWith('http') ? '_blank' : undefined}
    rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
    aria-label={label}
    className="flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:opacity-80"
    style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
  >
    <Icon
      size={16}
      style={{ color: "#FFFFFF" }}
    />
  </a>
))}
</div>
        </div>

        {/* scroll-to-top */}
        <button
          onClick={scrollToTop}
          aria-label={t('footerNew.scrollToTop')}
          className="mt-10 flex h-11 w-11 items-center justify-center rounded-full shadow-sm transition-transform hover:-translate-y-0.5"
          style={{ backgroundColor: "#FFFFFF" }}
        >
          <IconArrowUp width={20} height={20} style={{ color: "#000000" }} />
        </button>

        <div className="mt-8 text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
          © {new Date().getFullYear()} Vizcorn. {t('footerNew.rights')}
        </div>
      </div>
    </footer>
  );
}
